import { useState, useEffect } from "react";
import { Link } from "@remix-run/react";
import "~/styles/date-converter.css";
// @ts-expect-error - moment-hijri types not available
import moment from 'moment-hijri';
import { FaCopy, FaCheck, FaShare } from 'react-icons/fa';

// تحويل هجري إلى ميلادي باستخدام moment-hijri
function convertHijriToGregorian(hYear: number, hMonth: number, hDay: number) {
  try {
    const m = moment(`${hYear}-${hMonth}-${hDay}`, 'iYYYY-iM-iD');
    if (!m.isValid()) throw new Error('Invalid Hijri date');
    return {
      year: m.year(),
      month: m.month() + 1,
      day: m.date(),
      dayName: m.locale('ar-SA').format('dddd'),
      monthName: m.locale('ar-SA').format('MMMM')
    };
  } catch (error) {
    console.error('Error converting Hijri to Gregorian:', error);
    return { year: 0, month: 0, day: 0, dayName: '', monthName: '' };
  }
}

// تحويل ميلادي إلى هجري باستخدام moment-hijri
function convertGregorianToHijri(gYear: number, gMonth: number, gDay: number) {
  try {
    const m = moment(`${gYear}-${gMonth}-${gDay}`, 'YYYY-M-D');
    if (!m.isValid()) throw new Error('Invalid Gregorian date');
    return {
      year: Number(m.format('iYYYY')),
      month: Number(m.format('iM')),
      day: Number(m.format('iD')),
      dayName: m.locale('ar-SA').format('dddd'),
      monthName: m.locale('ar-SA').format('iMMMM')
    };
  } catch (error) {
    console.error('Error converting Gregorian to Hijri:', error);
    return { year: 0, month: 0, day: 0, dayName: '', monthName: '' };
  }
}

function getDayOfYear(year: number, month: number, day: number): number {
  const date = new Date(year, month - 1, day);
  const start = new Date(date.getFullYear(), 0, 0);
  const diff = date.getTime() - start.getTime();
  const oneDay = 1000 * 60 * 60 * 24;
  return Math.floor(diff / oneDay);
}

function getWeekOfYear(year: number, month: number, day: number): number {
  const date = new Date(year, month - 1, day);
  const start = new Date(date.getFullYear(), 0, 1);
  const diff = (date.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
  return Math.ceil((diff + start.getDay() + 1) / 7);
}

function getHijriMonthNames(lang: 'ar'|'en'): string[] {
  if (lang === 'ar') {
    return [
      'محرم', 'صفر', 'ربيع الأول', 'ربيع الآخر', 'جمادى الأولى', 'جمادى الآخرة',
      'رجب', 'شعبان', 'رمضان', 'شوال', 'ذو القعدة', 'ذو الحجة'
    ];
  } else {
    return [
      'Muharram', 'Safar', 'Rabi\' al-Awwal', 'Rabi\' al-Thani', 'Jumada al-Awwal', 'Jumada al-Thani',
      'Rajab', 'Sha\'ban', 'Ramadan', 'Shawwal', 'Dhu al-Qadah', 'Dhu al-Hijjah'
    ];
  }
}

function getGregorianMonthNames(lang: 'ar'|'en'): string[] {
  if (lang === 'ar') {
    return [
      'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
      'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
    ];
  } else {
    return [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
  }
}

function getDaysInMonth(year: number, month: number, isHijri: boolean = false): number {
  if (isHijri) {
    // للتقويم الهجري، معظم الأشهر 30 يوم
    const hijriMonthDays = [30, 29, 30, 29, 30, 29, 30, 29, 30, 29, 30, 29];
    return hijriMonthDays[month - 1] || 30;
  } else {
    return new Date(year, month, 0).getDate();
  }
}

const translations = {
  ar: {
    title: "محول التاريخ",
    subtitle: "حوّل بين التاريخ الهجري والميلادي بسهولة واحصل على معلومات إضافية",
    selectType: "نوع التحويل:",
    hijriToGregorian: "من هجري إلى ميلادي",
    gregorianToHijri: "من ميلادي إلى هجري",
    enterDate: "أدخل التاريخ:",
    day: "اليوم",
    month: "الشهر",
    year: "السنة",
    convert: "تحويل",
    result: "النتيجة",
    weekOfYear: "رقم الأسبوع من السنة",
    dayOfYear: "رقم اليوم من السنة",
    dayName: "اسم اليوم",
    hijri: "هجري",
    gregorian: "ميلادي",
    selectDay: "اختر اليوم",
    selectMonth: "اختر الشهر",
    selectYear: "اختر السنة",
    backToHome: "العودة إلى الصفحة الرئيسية"
  },
  en: {
    title: "Date Converter",
    subtitle: "Convert between Hijri and Gregorian dates easily and get extra info",
    selectType: "Conversion type:",
    hijriToGregorian: "Hijri to Gregorian",
    gregorianToHijri: "Gregorian to Hijri",
    enterDate: "Enter date:",
    day: "Day",
    month: "Month",
    year: "Year",
    convert: "Convert",
    result: "Result",
    weekOfYear: "Week of year",
    dayOfYear: "Day of year",
    dayName: "Day name",
    hijri: "Hijri",
    gregorian: "Gregorian",
    selectDay: "Select Day",
    selectMonth: "Select Month",
    selectYear: "Select Year",
    backToHome: "Back to Main Page"
  }
};

interface ConversionResult {
  year: number;
  month: number;
  day: number;
  week: number;
  dayOfYear: number;
  dayName: string;
  label: string;
}

export default function DateConverter() {
  const [lang, setLang] = useState<'ar'|'en'>("ar");
  const [type, setType] = useState<'h2g'|'g2h'>("h2g");
  const [day, setDay] = useState(1);
  const [month, setMonth] = useState(1);
  const [year, setYear] = useState(1445);
  const [result, setResult] = useState<ConversionResult | null>(null);
  const [isConverting, setIsConverting] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const t = translations[lang];
  const hijriMonthNames = getHijriMonthNames(lang);
  const gregorianMonthNames = getGregorianMonthNames(lang);
  const currentYear = new Date().getFullYear();
  const currentHijriYear = 1445; // تقريبي

  // إنشاء قوائم الأيام والأشهر والسنوات
  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const gregorianYears = Array.from({ length: 100 }, (_, i) => currentYear - 50 + i);
  const hijriYears = Array.from({ length: 100 }, (_, i) => currentHijriYear - 50 + i);

  // تحديث عدد أيام الشهر عند تغيير الشهر أو السنة
  useEffect(() => {
    const maxDays = getDaysInMonth(year, month, type === 'h2g');
    if (day > maxDays) {
      setDay(maxDays);
    }
  }, [year, month, type, day]);

  // تحديث السنة الافتراضية عند تغيير نوع التحويل
  useEffect(() => {
    if (type === 'h2g') {
      setYear(currentHijriYear);
    } else {
      setYear(currentYear);
    }
  }, [type]);

  // تحديث اتجاه الصفحة واللغة
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    if (typeof window !== 'undefined') {
      localStorage.setItem('pageLang', lang);
    }
  }, [lang]);

  const handleConvert = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsConverting(true);
    
    // محاكاة تأخير للحصول على تأثير أفضل
    await new Promise(resolve => setTimeout(resolve, 500));
    
    try {
      let converted, week, dayOfYear, dayName;
      
      if(type === "h2g") {
        converted = convertHijriToGregorian(year, month, day);
        week = getWeekOfYear(converted.year, converted.month, converted.day);
        dayOfYear = getDayOfYear(converted.year, converted.month, converted.day);
        dayName = converted.dayName;
        setResult({
          year: converted.year,
          month: converted.month,
          day: converted.day,
          week,
          dayOfYear,
          dayName,
          label: t.gregorian
        });
      } else {
        converted = convertGregorianToHijri(year, month, day);
        week = getWeekOfYear(year, month, day);
        dayOfYear = getDayOfYear(year, month, day);
        dayName = converted.dayName;
        setResult({
          year: converted.year,
          month: converted.month,
          day: converted.day,
          week,
          dayOfYear,
          dayName,
          label: t.hijri
        });
      }
    } catch (error) {
      console.error('Conversion error:', error);
    } finally {
      setIsConverting(false);
    }
  };

  const toggleLanguage = () => {
    const newLang = lang === 'ar' ? 'en' : 'ar';
    setLang(newLang);
  };

  return (
    <div className="date-converter-app" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      {/* Header with Glass Effect - Matching Age Calculator */}
      <header className="w-full sticky top-0 z-30 bg-white/70 dark:bg-gray-900/80 backdrop-blur-md shadow-md border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-2xl mx-auto flex items-center justify-between px-3 py-2 sm:px-6 sm:py-3">
          {/* زر الرجوع */}
          <Link
            to="/"
            className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-200 shadow hover:bg-purple-200 dark:hover:bg-purple-800 transition-all"
            aria-label={t.backToHome}
          >
            {lang === 'ar' ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h17" />
              </svg>
            )}
          </Link>
          {/* اسم التطبيق */}
          <span className="text-lg sm:text-xl font-extrabold text-purple-700 dark:text-purple-200 tracking-tight select-none">
            {t.title}
          </span>
          {/* زر اللغة */}
          <button
            onClick={toggleLanguage}
            className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-bold shadow hover:bg-gray-300 dark:hover:bg-gray-700 transition-all"
            aria-label={lang === 'ar' ? "Switch to English" : "التبديل إلى العربية"}
          >
            {lang === 'ar' ? 'EN' : 'عربي'}
          </button>
        </div>
      </header>

      {/* تم إزالة قسم الهيرو بالكامل لزيادة البساطة وسرعة الاستجابة */}

      {/* Main Content Area */}
      <main className="dc-main-content container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {/* عنوان وشرح مختصر */}
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold mb-2 text-purple-700 dark:text-purple-300">{t.title}</h1>
            <p className="text-base text-gray-600 dark:text-gray-300">{t.subtitle}</p>
          </div>
          <div className="dc-card dc-form p-6 mb-8">
            <form className="space-y-6" onSubmit={handleConvert} autoComplete="off">
              <div>
                <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-4">{t.selectType}</h2>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4 mb-6">
                  <button 
                    type="button" 
                    onClick={() => setType('h2g')}
                    className={`dc-calendar-btn flex-1 sm:flex-none ${type === 'h2g' ? 'active' : ''}`}
                    aria-pressed={type === 'h2g'}
                  >
                    <span role="img" aria-label="Hijri Calendar" className={lang === 'ar' ? 'ml-2' : 'mr-2'}>🌙</span> 
                    {t.hijriToGregorian}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setType('g2h')}
                    className={`dc-calendar-btn flex-1 sm:flex-none ${type === 'g2h' ? 'active' : ''}`}
                    aria-pressed={type === 'g2h'}
                  >
                    <span role="img" aria-label="Gregorian Calendar" className={lang === 'ar' ? 'ml-2' : 'mr-2'}>☀️</span> 
                    {t.gregorianToHijri}
                  </button>
                </div>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-4">
                  {t.enterDate}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="day" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t.day}
                    </label>
                    <select 
                      id="day" 
                      value={day} 
                      onChange={e => setDay(Number(e.target.value))}
                      className="dc-input w-full"
                      title={t.selectDay}
                    >
                      {days.map(d => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="month" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t.month}
                    </label>
                    <select 
                      id="month" 
                      value={month} 
                      onChange={e => setMonth(Number(e.target.value))}
                      className="dc-input w-full"
                      title={t.selectMonth}
                    >
                      {months.map(m => (
                        <option key={m} value={m}>
                          {type === 'h2g' ? hijriMonthNames[m-1] : gregorianMonthNames[m-1]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="year" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t.year}
                    </label>
                    <select 
                      id="year" 
                      value={year} 
                      onChange={e => setYear(Number(e.target.value))}
                      className="dc-input w-full"
                      title={t.selectYear}
                    >
                      {(type === 'h2g' ? hijriYears : gregorianYears).map(y => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <button
                type="submit"
                disabled={isConverting}
                className="dc-btn w-full py-3 text-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isConverting ? (lang === 'ar' ? 'جاري التحويل...' : 'Converting...') : t.convert}
              </button>
            </form>
          </div>
          {result && (
            <div className="flex justify-center mt-8">
              <div className="w-full max-w-md bg-white/90 dark:bg-gray-900 rounded-2xl shadow-md p-5 text-center relative animate-fade-in border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-3">{t.result}</h3>
                {/* التاريخ النصي + الرقمي في سطر واحد */}
                <div className="text-xl font-bold text-gray-900 dark:text-white mb-2 flex flex-col items-center gap-1">
                  <span>
                    {type === 'h2g'
                      ? `${result.day} ${gregorianMonthNames[result.month - 1]} ${result.year}`
                      : `${result.day} ${hijriMonthNames[result.month - 1]} ${result.year}`}
                  </span>
                  <span className="text-base font-mono text-gray-700 dark:text-gray-200">
                    {`${String(result.day).padStart(2, '0')}/${String(result.month).padStart(2, '0')}/${result.year}`}
                  </span>
                </div>
                <div className="text-base text-gray-600 dark:text-gray-300 mb-4">{result.dayName}</div>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  {/* زر نسخ موحد */}
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(
                        (type === 'h2g'
                          ? `${result.day} ${gregorianMonthNames[result.month - 1]} ${result.year}`
                          : `${result.day} ${hijriMonthNames[result.month - 1]} ${result.year}`)
                        + `\n${String(result.day).padStart(2, '0')}/${String(result.month).padStart(2, '0')}/${result.year}`
                      );
                      setCopied(true);
                      setTimeout(() => setCopied(false), 1500);
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full bg-gray-200 dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700 text-gray-800 dark:text-white font-bold shadow-sm transition-all duration-200 ${copied ? 'bg-green-500 text-white' : ''}`}
                  >
                    {copied ? <FaCheck /> : <FaCopy />} {copied ? (lang === 'ar' ? 'تم النسخ' : 'Copied!') : (lang === 'ar' ? 'نسخ النتيجة' : 'Copy result')}
                  </button>
                  {/* زر مشاركة موحد */}
                  <button
                    onClick={() => {
                      const shareText =
                        (type === 'h2g'
                          ? `${result.day} ${gregorianMonthNames[result.month - 1]} ${result.year}`
                          : `${result.day} ${hijriMonthNames[result.month - 1]} ${result.year}`)
                        + `\n${String(result.day).padStart(2, '0')}/${String(result.month).padStart(2, '0')}/${result.year}`;
                      const waUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
                      const twUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
                      if (navigator.share) {
                        navigator.share({ text: shareText });
                      } else {
                        // فتح نافذة اختيار
                        if (window.confirm(lang === 'ar' ? 'مشاركة على واتساب؟ (إلغاء = تويتر)' : 'Share on WhatsApp? (Cancel = Twitter)')) {
                          window.open(waUrl, '_blank');
                        } else {
                          window.open(twUrl, '_blank');
                        }
                      }
                    }}
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500 hover:bg-blue-600 text-white font-bold shadow-sm transition-all duration-200"
                  >
                    <FaShare /> {lang === 'ar' ? 'مشاركة' : 'Share'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <footer className="text-center py-8 px-4 text-sm text-gray-500 dark:text-gray-400">
        <div className="space-y-2">
          <p className="break-words">جميع الحسابات تعتمد على التاريخ الحالي من الخادم لضمان الدقة</p>
          <p className="break-words">التقويم الهجري المعتمد هو تقويم أم القرى</p>
          <p className="break-words">
          <a 
            href="https://www.snapchat.com/@zk9c"
            target="_blank" 
            rel="noopener noreferrer" 
            className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors duration-300"
          >
            صنع بكل ❤️ في 🇸🇦
          </a>
        </p>
        </div>
      </footer>
    </div>
  );
} 